import { type components } from '@heidi/schema/ml-scribe';
import { type QuerySessionResponse } from '@heidi/session/contracts';
import { isEqual } from 'lodash-es';

import { type ConfigResolution } from './resolve-config';

const REQUEST_FIELDS = [
  'generation_method',
  'addition',
  'ai_command_text',
  'ai_command_id',
  'template_id',
  'voice_style',
  'custom_style',
  'brain',
  'dictation_cleanup_mode',
  'content_type',
  'platform',
  'is_personalization_applied',
  'ignore_backup',
  'demo_mode',
] as const satisfies readonly (keyof components['schemas']['GenerateConsultNoteRequest'])[];

const OPTIMISTIC_FIELDS = [
  'voice_style',
  'brain',
  'template_id',
  'ai_command_text',
  'generation_method',
] as const;

export type OptimisticAttributes = Pick<
  QuerySessionResponse['consult_note'],
  (typeof OPTIMISTIC_FIELDS)[number]
>;

// Closed labels only. Unknown request fields are classified as shape drift,
// never copied into diagnostics. All comparisons happen locally.
export type ConfigMismatchField =
  | `request.${(typeof REQUEST_FIELDS)[number]}`
  | 'request.shape'
  | `optimistic.${(typeof OPTIMISTIC_FIELDS)[number]}`;

export type ConfigShadowOutcome = (
  | { kind: 'matched' }
  | { kind: 'mismatch'; fields: readonly ConfigMismatchField[] }
  | { kind: 'diagnostic-failed' }
) & { attributeComparisonSkipped: boolean };

export type ConfigObservation = {
  request: components['schemas']['GenerateConsultNoteRequest'];
  previousAttributes: OptimisticAttributes | undefined;
  optimisticAttributes: OptimisticAttributes | undefined;
};

export function compareConfig(
  shadow: ConfigResolution,
  actual: ConfigObservation
): ConfigShadowOutcome {
  const fields: ConfigMismatchField[] = [];
  for (const field of REQUEST_FIELDS) {
    if (!isEqual(shadow.request[field], actual.request[field])) {
      fields.push(`request.${field}`);
    }
  }
  const allowedKeys = new Set<string>(REQUEST_FIELDS);
  if (
    [shadow.request, actual.request].some((request) =>
      Object.keys(request).some((key) => !allowedKeys.has(key))
    )
  )
    fields.push('request.shape');

  const previous = actual.previousAttributes;
  const optimistic = actual.optimisticAttributes;
  const patch = shadow.optimisticPatch;
  const expected = previous
    ? {
        voice_style: patch.voiceStyle ?? previous.voice_style,
        brain: patch.brain ?? previous.brain,
        template_id: patch.templateId ?? previous.template_id,
        ai_command_text: patch.aiCommandText ?? previous.ai_command_text,
        generation_method: patch.generationMethod ?? previous.generation_method,
      }
    : undefined;
  for (const field of OPTIMISTIC_FIELDS) {
    if (!isEqual(expected?.[field], optimistic?.[field])) {
      fields.push(`optimistic.${field}`);
    }
  }
  const attributeComparisonSkipped = previous === undefined;
  return fields.length
    ? { kind: 'mismatch', fields, attributeComparisonSkipped }
    : { kind: 'matched', attributeComparisonSkipped };
}
